import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { LogOut, Menu, Moon, Sun, X } from "lucide-react";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { SHELL_CONTENT_CLASS } from "@/components/layout/shellContent";
import logo from "@/assets/habits365greek-logo.png";
import { PortalSidebar } from "@/components/navigation/PortalSidebar";
import { PortalFooter } from "@/components/PortalFooter";
import { useAuth } from "@/contexts/AuthContext";
import { SUPPLIER_NAV } from "../nav";

/**
 * SupplierShell — the supplier portal's layout chrome. Mirrors `DesignerShell`
 * so every portal looks and behaves the same, feeding the SHARED `PortalSidebar`
 * the supplier nav registry and brand. Suppliers sign in through the same auth
 * context as staff; which supplier they belong to comes from the API session.
 *
 * Sign-out is the ONLY place this portal ends the session, and only on an
 * explicit user click — guards redirect, never sign out. It uses the same
 * `useAuth().logout` as the staff portal: suppliers hold the app JWT, which a
 * Supabase sign-out would leave behind.
 *
 * NOTHING here may call a staff endpoint: the API refuses a supplier login
 * everything but `/api/supplier-portal/**`, `GET /api/me` and
 * `GET /api/platform/**`. The name comes from the `/me` session already loaded.
 */

interface SupplierShellProps {
  children: React.ReactNode;
}

const SupplierBrand = () => (
  <Link to="/supplier/board" className="flex items-center gap-2">
    <img
      src={logo}
      alt="Habits 365 Greek"
      className="h-7 object-contain dark:invert dark:brightness-200"
    />
    <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-primary">
      Supplier
    </span>
  </Link>
);

const SupplierSidebarFooter = () => (
  <div className="text-xs text-muted-foreground">
    Questions about an order?
    <br />
    <a href="mailto:hello@habits365greek.com" className="text-primary hover:underline">
      hello@habits365greek.com
    </a>
  </div>
);

export const SupplierShell = ({ children }: SupplierShellProps) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);

  const fullName = user?.full_name?.trim() || user?.email || "Supplier";

  const handleSignOut = async () => {
    /** Explicit, user-initiated only — see file header. `logout` clears the tokens and goes to /signin. */
    await logout();
    toast.success("Signed out");
  };

  const initials =
    fullName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "?";

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Shared desktop sidebar */}
      <div className="sticky top-0 hidden h-screen md:block">
        <PortalSidebar
          className="h-screen"
          ariaLabel="Supplier navigation"
          groups={[{ items: SUPPLIER_NAV }]}
          header={<SupplierBrand />}
          footer={<SupplierSidebarFooter />}
        />
      </div>

      {/* Main column */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-sticky border-b border-border bg-navbar/95 text-navbar-foreground backdrop-blur-sm">
          <div className="flex h-16 items-center gap-4 px-4 sm:px-6 lg:px-8">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle navigation"
              aria-expanded={mobileOpen}
              aria-controls="supplier-mobile-nav"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>

            <Link to="/supplier/board" className="flex items-center gap-2 md:hidden">
              <img
                src={logo}
                alt="Habits 365 Greek"
                className="h-8 object-contain dark:invert dark:brightness-200"
              />
            </Link>

            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                aria-label="Toggle theme"
              >
                {theme === "dark" ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary transition-colors hover:bg-primary/15"
                    aria-label="Account menu"
                  >
                    {initials}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="space-y-0.5">
                    <div className="truncate text-sm font-semibold">{fullName}</div>
                    <div className="truncate text-xs font-normal text-muted-foreground">
                      {user?.email}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleSignOut}
                    className="text-destructive focus:text-destructive"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Mobile nav drawer — reads the SAME SUPPLIER_NAV (no second list) */}
          {mobileOpen && (
            <nav
              id="supplier-mobile-nav"
              className="border-t border-border bg-background/95 p-3 backdrop-blur md:hidden"
            >
              {SUPPLIER_NAV.map((item) => {
                const Icon = item.icon;
                const active =
                  location.pathname === item.path ||
                  location.pathname.startsWith(`${item.path}/`);
                return (
                  <NavLink
                    key={item.key}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "flex h-10 items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors",
                      active
                        ? "bg-sidebar-active text-sidebar-active-foreground"
                        : "text-muted-foreground hover:bg-sidebar-hover hover:text-sidebar-foreground",
                    )}
                  >
                    <Icon className={cn("h-4 w-4 flex-shrink-0", item.iconClass)} />
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>
          )}
        </header>

        {/*
          Page content — full-width, the staff dashboard's gutter exactly, so
          the supplier board and order page sit where the Habits team's view of
          them does (and the order page's full-bleed sticky header, which
          cancels `sm:px-6`, reaches the edge at every width).
        */}
        <main className={cn("flex flex-1 flex-col overflow-auto", SHELL_CONTENT_CLASS)}>
          {children}
        </main>

        <PortalFooter />
      </div>
    </div>
  );
};

export default SupplierShell;
